import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ChevronLeft, User, Briefcase, ArrowRight } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('Debes aceptar los términos de uso y privacidad');
      return;
    }

    setLoading(true);
    try {
      const apiRole = role === 'user' ? 'user' : role;
      await register(email, password, `${firstName.trim()} ${lastName.trim()}`, apiRole);
      toast.success('Cuenta creada exitosamente');
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (error) {
      setError(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!acceptedTerms) {
      setError('Debes aceptar los términos de uso y privacidad');
      return;
    }
    localStorage.setItem('utentik_register_role', role);
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-gray-100">
        <button
          onClick={() => step === 2 ? setStep(1) : navigate('/prelanzamiento')}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          data-testid="register-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <Link to="/" className="block text-center mb-8">
            <img src="/utentik-logo.png" alt="utentik" className="h-7 mx-auto" />
          </Link>

          {step === 1 ? (
            /* ===== STEP 1: Role Selection ===== */
            <div className="animate-fade-in">
              <h1 className="font-rubik text-2xl font-bold text-center text-gray-500 mb-2">
                {t('auth.registerTitle')}
              </h1>
              <p className="text-center text-gray-400 mb-10">
                Selecciona tu perfil para comenzar
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                <button
                  type="button"
                  onClick={() => setRole('user')}
                  className={`p-5 rounded-2xl border-2 text-left transition-all ${
                    role === 'user'
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="role-user-btn"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${role === 'user' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <User className="w-5 h-5" />
                  </div>
                  <p className={`font-semibold text-sm ${role === 'user' ? 'text-gray-800' : 'text-gray-500'}`}>
                    {t('auth.asUser')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">Quiero descubrir y reservar actividades</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('creator')}
                  className={`p-5 rounded-2xl border-2 text-left transition-all ${
                    role === 'creator'
                      ? 'border-amber-400 bg-amber-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="role-creator-btn"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${role === 'creator' ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <p className={`font-semibold text-sm ${role === 'creator' ? 'text-gray-800' : 'text-gray-500'}`}>
                    {t('auth.asCreator')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">Quiero publicar mis talleres y experiencias</p>
                </button>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!role}
                className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold disabled:opacity-40"
                data-testid="role-next-btn"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-center text-gray-400 mt-8">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-primary font-medium" data-testid="login-link">
                  {t('auth.loginHere')}
                </Link>
              </p>
            </div>
          ) : (
            /* ===== STEP 2: Registration Form ===== */
            <div className="animate-fade-in">
              <h1 className="font-rubik text-2xl font-bold text-center text-gray-500 mb-2">
                Crea tu cuenta
              </h1>
              <p className="text-center text-gray-400 mb-8">
                {role === 'creator' ? 'Registro como creador' : 'Registro como usuario'}
              </p>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm mb-6" data-testid="error-message">
                  {error}
                </div>
              )}

              {/* Google Login */}
              <Button
                variant="outline"
                className="w-full rounded-xl py-6 border-gray-200"
                onClick={handleGoogleLogin}
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Crear cuenta con Google
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-gray-400">o con email</span>
                </div>
              </div>

              {/* Register Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName" className="text-sm font-medium text-gray-500">Nombre</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Nombre"
                      className="mt-2 input-airbnb"
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm font-medium text-gray-500">Apellido</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Apellido"
                      className="mt-2 input-airbnb"
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-500">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="mt-2 input-airbnb"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-gray-500">{t('auth.password')}</Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 caracteres"
                      className="input-airbnb pr-12"
                      required
                      minLength={8}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Terms acceptance */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-xs text-gray-400 leading-relaxed">
                    Acepto los{' '}
                    <Link to="/legal" className="text-primary underline">Términos de uso</Link>
                    {' '}y la{' '}
                    <Link to="/legal" className="text-primary underline">Política de privacidad</Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creando cuenta...' : t('auth.registerButton')}
                </Button>
              </form>

              <p className="text-center text-gray-400 mt-8">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-primary font-medium" data-testid="login-link">
                  {t('auth.loginHere')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
